import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { ChevronLeft } from 'lucide-react';

import { computeRecords } from '../utils/records';
import { buildExerciseHistory, groupSessionExercises, historyWeeks, sessionWorkout, visibleWeeks } from '../utils/progress';
import { GET_ME, GET_WORKOUTS, GET_WORKOUT_LOGS } from '../graphql/queries';
import { DELETE_WORKOUT_LOG } from '../graphql/mutations';
import { trackEvent, trackPageView } from '../utils/gt';
import { listChanged } from '../utils/quietRefresh';

import { WeekChart } from '../components/progress/ProgressCharts';
import { SessionFeed, SessionPage } from '../components/progress/SessionViews';
import { ExerciseDetail, ExerciseList } from '../components/progress/ExerciseViews';
import { useProgressFormat } from '../components/progress/useProgressFormat';

import '../styles/pages/progresso.css';
import { PreviewArt } from '../components/app/PreviewArt';

const PAGE_SIZE = 6;
const TABS = ['sessions', 'exercises'];

// This week in three numbers, beside (desktop) or over (phones) the last 12 weeks
const SummaryCard = ({ week, logs, isDesktop }) => {
  const format = useProgressFormat();
  const { ui } = format;
  return (
    <section className="mt-pg-summary" aria-labelledby="mt-pg-summary-title">
      <div className="mt-pg-summary__week">
        <h2 id="mt-pg-summary-title">{ui('thisWeek')}</h2>
        <dl className="mt-pg-summary__stats">
          <div>
            <dt>{ui('workouts', { count: week.count })}</dt>
            <dd>{week.count}</dd>
          </div>
          <div>
            <dt>{ui('trained')}</dt>
            <dd>{format.trainingTime(week.duration)}</dd>
          </div>
          <div>
            <dt>{ui('volume')}</dt>
            <dd>
              {format.integer(week.volume)}
              <small> kg</small>
            </dd>
          </div>
        </dl>
      </div>
      <WeekChart logs={logs} isDesktop={isDesktop} />
    </section>
  );
};

/**
 * Progresso (owner's pick D, 29/10/2026): the week and the last 12 weeks on top, then Treinos | Exercícios.
 * Treinos is a feed of sessions as Hevy shows it, one card a row; a session opens on its own page. Exercícios lists
 * every exercise with its best set and trend; one opens its evolution (beside the list on desktop, its own page on
 * phones). What's open lives in the URL (?s=<log>, ?tab=exercises, ?e=<exercise>), so back closes it and the page
 * underneath comes back where it was. Records, best sets, 1RM and trends are worked out here, from the logs.
 */
export const Progress = () => {
  const { t } = useTranslation();
  const format = useProgressFormat();
  const { ui } = format;
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });

  const [deletingLogId, setDeletingLogId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Where each page of this one was scrolled to, so going back lands where it was
  const positions = useRef({});
  // What opened the page on top (a session card, an exercise row), to take the focus back to it
  const opener = useRef(null);
  const backRef = useRef(null);
  const tabsRef = useRef(null);
  const detailRef = useRef(null);

  const { data: dataMe } = useQuery(GET_ME);
  const { data: dataWorkouts, loading: isLoadingWorkouts } = useQuery(GET_WORKOUTS);
  // From memory when coming back, read first after a session was logged or deleted: the list never comes back without it
  const [logsPolicy] = useState(() => (listChanged('WorkoutLogs') ? 'network-only' : 'cache-first'));
  const { data: dataLogs, loading: isLoadingLogs, refetch: refetchLogs } = useQuery(GET_WORKOUT_LOGS, {
    fetchPolicy: logsPolicy,
    nextFetchPolicy: 'cache-first',
  });
  const logs = useMemo(() => dataLogs?.workoutLogs?.data || [], [dataLogs]);
  const workouts = dataWorkouts?.workouts?.data;
  const me = dataMe?.me;
  const isPro = me?.plan === 'pro' && (me?.subscriptionStatus === 'active' || me?.subscriptionStatus === 'trialing');

  const [deleteWorkoutLogMutation] = useMutation(DELETE_WORKOUT_LOG);

  useEffect(() => {
    trackPageView('progress', `${t('appUrl')}/progress`, '/progress');
  }, [t]);

  const groupsByLog = useMemo(() => Object.fromEntries(logs.map((log) => [log.id, groupSessionExercises(log.attributes?.exercises)])), [logs]);
  const recordsByLog = useMemo(() => computeRecords(logs), [logs]);
  const exercises = useMemo(() => buildExerciseHistory(logs, groupsByLog, recordsByLog), [logs, groupsByLog, recordsByLog]);
  const weeks = useMemo(() => historyWeeks(logs), [logs]);
  const feed = useMemo(() => visibleWeeks(weeks, visibleCount), [weeks, visibleCount]);

  const sessionId = searchParams.get('s');
  const exerciseName = searchParams.get('e');
  const tab = searchParams.get('tab') === 'exercises' || (exerciseName && !sessionId) ? 'exercises' : 'sessions';
  const sessionLog = (sessionId && logs.find((log) => log.id === sessionId)) || null;
  const openExercise = (exerciseName && exercises.find((exercise) => exercise.name === exerciseName)) || null;
  // Desktop shows one exercise beside the list at all times: the chosen one, or the one trained last
  const shownExercise = openExercise || (isDesktop ? exercises[0] : null);
  const view = !isDesktop && openExercise ? 'exercise' : sessionLog ? 'session' : 'main';

  const searchOf = (params) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value)).toString();
    return query ? `?${query}` : '';
  };
  const sessionHref = (logId) => `${location.pathname}${searchOf({ s: logId })}`;

  // A page on top is a step in the history (back closes it); switching the open exercise on desktop isn't
  const go = (params, { replace = false, state = {} } = {}) => {
    positions.current[location.key] = window.scrollY;
    navigate({ search: searchOf(params) }, { replace, state: replace ? location.state : { fromProgress: true, ...state } });
  };

  // Back to the page underneath: through the history when this page opened it, otherwise (a shared link) up a level
  const goBack = () => {
    if (location.state?.fromProgress) {
      navigate(-1);
      return;
    }
    if (view === 'exercise') go({ s: sessionId, tab: sessionId ? null : 'exercises' }, { replace: true });
    else go({ tab: tab === 'exercises' ? 'exercises' : null }, { replace: true });
  };

  const openSession = (logId) => {
    trackEvent('progress_open_session', 'Progress - Open Session', 'progress', true);
    opener.current = view === 'main' ? `[data-session-link="${logId}"]` : opener.current;
    // On phones a session opened from an exercise's history takes that page's place
    go({ s: logId }, { replace: view === 'exercise' });
  };

  const openExerciseView = (name) => {
    trackEvent('progress_open_exercise', 'Progress - Open Exercise', 'progress', true);
    if (!isDesktop) {
      opener.current = view === 'main' ? `[data-exercise-link="${name.replace(/["\\]/g, '\\$&')}"]` : opener.current;
      go({ s: sessionId, tab: sessionId ? null : 'exercises', e: name });
      return;
    }
    if (view === 'session') {
      // Desktop: the exercise opens beside the list, a step forward from the session
      go({ tab: 'exercises', e: name }, { state: { toTabs: true } });
      return;
    }
    go({ tab: 'exercises', e: name }, { replace: true });
    if (detailRef.current && detailRef.current.getBoundingClientRect().top < 0) tabsRef.current?.scrollIntoView({ block: 'start' });
  };

  const selectTab = (next) => {
    if (next === tab) return;
    if (next === 'exercises') trackEvent('progress_tab_exercises', 'Progress - Tab Exercises', 'progress', true);
    go({ tab: next === 'exercises' ? 'exercises' : null }, { replace: true });
  };

  // Arrow keys move between the two tabs, as a tab list does
  const onTabsKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = TABS[(TABS.indexOf(tab) + 1) % TABS.length];
    selectTab(next);
    tabsRef.current?.querySelector(`[data-tab="${next}"]`)?.focus();
  };

  // A new page starts at the top (or, on desktop, at the tabs when an exercise opens from a session); going back
  // restores where the page underneath was
  useLayoutEffect(() => {
    const saved = positions.current[location.key];
    if (navigationType === 'POP' && saved !== undefined) window.scrollTo(0, saved);
    else if (navigationType === 'PUSH' && location.state?.toTabs) tabsRef.current?.scrollIntoView({ block: 'start' });
    else if (navigationType === 'PUSH') window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // The way back takes the focus on a page on top; back underneath, the card or row that opened it does
  useEffect(() => {
    if (view !== 'main') {
      backRef.current?.focus({ preventScroll: true });
    } else if (opener.current) {
      document.querySelector(opener.current)?.focus({ preventScroll: true });
      opener.current = null;
    }
  }, [view, sessionId, exerciseName]);

  // Resolves true when the log is gone, so the dialog can close; the session's page closes with it
  const handleDeleteLog = async (logId) => {
    setDeletingLogId(logId);
    try {
      await deleteWorkoutLogMutation({ variables: { workoutLogId: logId } });
      trackEvent('progress_delete_log', 'Progress - Delete Log', 'progress', true);
      await refetchLogs();
      opener.current = null;
      goBack();
      return true;
    } catch {
      toast.error(t('notifications.workoutLogDeleteError'), { toastId: 'workout-log-delete-error' });
      return false;
    } finally {
      setDeletingLogId(null);
    }
  };

  const isLoading = isLoadingWorkouts || isLoadingLogs;

  const backBar = (label) => (
    <button type="button" ref={backRef} className="mt-pg-back" onClick={goBack}>
      <ChevronLeft aria-hidden="true" />
      <span className="mt-sr-only">{ui('backTo')} </span>
      {label}
    </button>
  );

  if (!isLoading && view === 'exercise') {
    const label = sessionLog ? format.workoutTitle(sessionWorkout(sessionLog, workouts).name) : ui('title');
    return (
      <div className="mt-root mt-root--app mt-pg mt-pg--sub">
        {backBar(label)}
        <ExerciseDetail exercise={openExercise} workouts={workouts} level={1} isDesktop={isDesktop} onOpenSession={openSession} />
      </div>
    );
  }

  if (!isLoading && view === 'session') {
    return (
      <div className="mt-root mt-root--app mt-pg mt-pg--sub">
        {backBar(ui('title'))}
        <SessionPage
          key={sessionLog.id}
          log={sessionLog}
          groups={groupsByLog[sessionLog.id] || []}
          records={recordsByLog[sessionLog.id]}
          workouts={workouts}
          isDeleting={deletingLogId === sessionLog.id}
          onDelete={handleDeleteLog}
          onOpenExercise={openExerciseView}
        />
      </div>
    );
  }

  let body;
  if (isLoading) {
    body = (
      <div className="mt-builder__status" aria-busy="true">
        <span className="mt-builder__spinner" aria-hidden="true" />
      </div>
    );
  } else if (logs.length === 0) {
    // Logging is Pro: a free account sees what Pro adds; a Pro one, where its first session will show up
    const showPro = !!me && !isPro;
    body = (
      <section className="mt-pg-empty">
        <PreviewArt kind="bars" badge={showPro ? 'lock' : undefined} />
        <h2 className="mt-pg-empty__title">{showPro ? ui('proTitle') : ui('emptyTitle')}</h2>
        <p className="mt-pg-empty__text">{showPro ? ui('proBody') : ui('emptyBody')}</p>
        <button
          type="button"
          className="mt-btn mt-btn--primary mt-pg-empty__cta"
          onClick={() => {
            if (showPro) trackEvent('progress_see_pro', 'Progress - See Pro', 'progress', true);
            navigate(showPro ? '/plans' : '/workouts');
          }}
        >
          {showPro ? ui('proCta') : ui('emptyCta')}
        </button>
      </section>
    );
  } else {
    const tabs = (
      <div ref={tabsRef} className="mt-segment mt-pg-tabs" role="tablist" aria-label={ui('viewBy')} onKeyDown={onTabsKeyDown}>
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`mt-pg-tab-${key}`}
            data-tab={key}
            aria-selected={tab === key}
            aria-controls={`mt-pg-panel-${key}`}
            tabIndex={tab === key ? 0 : -1}
            onClick={() => selectTab(key)}
          >
            {ui(key === 'sessions' ? 'tabSessions' : 'tabExercises')}
          </button>
        ))}
      </div>
    );
    body = (
      <>
        <SummaryCard week={weeks[0]} logs={logs} isDesktop={isDesktop} />
        {tab === 'sessions' ? (
          <div className="mt-pg-main mt-pg-main--sessions">
            {tabs}
            <div role="tabpanel" id="mt-pg-panel-sessions" aria-labelledby="mt-pg-tab-sessions">
              <SessionFeed
                weeks={feed.weeks}
                hasMore={feed.hasMore}
                groupsByLog={groupsByLog}
                recordsByLog={recordsByLog}
                workouts={workouts}
                sessionHref={sessionHref}
                onOpenSession={openSession}
                onLoadMore={() => setVisibleCount((count) => count + PAGE_SIZE)}
              />
            </div>
          </div>
        ) : (
          <div className="mt-pg-main mt-pg-main--exercises">
            <div className="mt-pg-main__side">
              {tabs}
              <div role="tabpanel" id="mt-pg-panel-exercises" aria-labelledby="mt-pg-tab-exercises">
                <ExerciseList exercises={exercises} currentName={isDesktop ? shownExercise?.name : null} onOpenExercise={openExerciseView} />
              </div>
            </div>
            {isDesktop && shownExercise && (
              <div ref={detailRef} className="mt-pg-main__detail">
                <ExerciseDetail key={shownExercise.name} exercise={shownExercise} workouts={workouts} isDesktop isCard onOpenSession={openSession} />
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mt-root mt-root--app mt-pg">
      <div className="mt-page-head mt-pg-head">
        <div>
          <h1 className="mt-page-head__title">{ui('title')}</h1>
          <p className="mt-page-head__sub">{ui('sub')}</p>
        </div>
      </div>
      {body}
    </div>
  );
};
